import React, { useCallback, useId, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { CloudUpload, Plus, RefreshCw } from "lucide-react";
import { useTranslation } from "../../context/Internationalization";
import { validateImageFile } from "./validators";
import UploadFilePreview from "./UploadFilePreview";
import { Button } from "../../shadcn/components/ui/button";
import { cn } from "../../shadcn/lib/utils";
import { Empty, EmptyMedia } from "../../shadcn/components/ui/empty";
import { FieldTitle } from "../../shadcn/components/ui/field";

const textClass = "text-sm text-muted-foreground";

const UploadIcon = () => (
  <EmptyMedia variant="icon" aria-hidden="true">
    <CloudUpload />
  </EmptyMedia>
);

const MultipleFilesUpload = ({
  label,
  name,
  value,
  onChange,
  acceptedFileTypes,
  singleFileUpload,
  maxSize = 15 * 1024 * 1024,
  error,
  maxSizeError,
  requirementsText,
  compact = singleFileUpload,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const labelId = useId();
  const [fileErrors, setFileErrors] = useState([]);
  // File extraction can finish after a Save disables the control.
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  // Normalize accept keys (react-dropzone typically expects object like { 'image/*': [] })
  const acceptKeys = acceptedFileTypes ? Object.keys(acceptedFileTypes) : [];

  const areImagesAllowed = acceptKeys.some((type) => type.startsWith("image/"));
  const areVideosAllowed = acceptKeys.some((type) => type.startsWith("video/"));
  const areDocumentsAllowed = acceptKeys.some((type) =>
    type.startsWith("application/")
  );

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      if (disabledRef.current) return;
      const newErrors = [];
      const newFiles = singleFileUpload
        ? []
        : Array.isArray(value)
        ? [...value]
        : [];

      const selectedFiles = [
        ...acceptedFiles,
        ...fileRejections.map(({ file }) => file),
      ];

      selectedFiles.forEach((selectedFile) => {
        const validationError = acceptedFileTypes
          ? validateImageFile(selectedFile, acceptedFileTypes, maxSize)
          : !Number.isInteger(selectedFile?.size) || selectedFile.size <= 0
          ? "empty"
          : selectedFile.size > maxSize
          ? "tooLarge"
          : null;

        if (validationError) {
          const errorKey = validationError === "unsupported"
            ? "invalid_file_type"
            : "invalid_file_size";
          const translatedError =
            validationError === "tooLarge" && maxSizeError
              ? maxSizeError
              : t(errorKey);
          if (!newErrors.includes(translatedError)) newErrors.push(translatedError);
          return;
        }

        if (fileRejections.some(({ file }) => file === selectedFile)) {
          const translatedError = t("invalid_file_type");
          if (!newErrors.includes(translatedError)) newErrors.push(translatedError);
          return;
        }

        if (singleFileUpload) {
          newFiles[0] = selectedFile;
        } else {
          newFiles.push(selectedFile);
        }
      });

      setFileErrors(newErrors);

      if (selectedFiles.length > 0 && newErrors.length === 0) {
        onChange({ target: { name, value: newFiles } });
      }
    },
    [
      onChange,
      name,
      value,
      acceptedFileTypes,
      singleFileUpload,
      maxSize,
      maxSizeError,
      t,
    ]
  );

  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    onDrop,
    disabled,
    accept: acceptedFileTypes || undefined,
    maxSize,
    noClick: value && value.length > 0,
    onError: () => {
      if (!disabledRef.current) setFileErrors([t("invalid_file_type")]);
    },
  });

  const addFile = () => {
    if (disabledRef.current) return;
    if (inputRef?.current) {
      inputRef.current.click();
    }
  };

  const removeFile = (index, event) => {
    event.stopPropagation();
    if (disabledRef.current) return;
    const newFiles = Array.isArray(value) ? [...value] : [];
    newFiles.splice(index, 1);
    onChange({ target: { name, value: newFiles } });
  };

  const renderPreview = (file, index) => (
    <UploadFilePreview key={index} file={file} compact={compact}
      removeDisabled={disabled}
      onRemove={(event) => removeFile(index, event)} />
  );

  const renderFileErrors = (errs) => {
    if (!errs || errs.length === 0) return null;
    return (
      <span className="text-sm text-destructive">
        {errs.map((error, index) => (
          <React.Fragment key={index}>
            {index > 0 && <br />}
            {error}
          </React.Fragment>
        ))}
      </span>
    );
  };

  const renderButton = () => {
    return (
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={(event) => { event.stopPropagation(); addFile(); }}
        className={cn("mt-4", compact ? "self-start" : "w-full")}
      >
        {singleFileUpload ? (
          <RefreshCw aria-hidden="true" />
        ) : (
          <Plus aria-hidden="true" />
        )}
        {singleFileUpload ? t("replace_file") : t("add_file")}
      </Button>
    );
  };

  const renderUploadArea = () => {
    return (
      <>
        <input {...getInputProps({ disabled, ...inputLabel })} />
        <UploadIcon />
        {isDragActive ? (
          <p>
            <span className={textClass}>{t("drop_files_here")}</span>
          </p>
        ) : (
          <>
            <div>
              <span
                className={cn(
                  "text-sm",
                  disabled
                    ? "text-muted-foreground"
                    : "cursor-pointer font-medium text-primary hover:underline"
                )}
              >
                {t("click_to_upload")}
              </span>{" "}
              <span className={textClass}>{t("drag_n_drop")}</span>
            </div>
            {fileErrors.length > 0 ? (
              renderFileErrors(fileErrors)
            ) : (
              <span className={textClass}>
                {areImagesAllowed && (
                  <strong>{requirementsText || t("image_upload_files")}</strong>
                )}
                {areVideosAllowed && (
                  <>
                    <br />
                    <strong>{t("video_upload_files")}</strong>
                  </>
                )}
                {areDocumentsAllowed && (
                  <>
                    <br />
                    <strong>{t("document_upload_files")}</strong>
                  </>
                )}
              </span>
            )}
          </>
        )}
      </>
    );
  };

  const hasFiles =
    Array.isArray(value) &&
    value.filter((item) => !!item && item !== "" && item !== null).length > 0;

  const cleanFiles = hasFiles ? value.filter(Boolean) : [];
  const hasLabel = label && label?.length > 0;
  // The file input had no name for screen readers.
  const inputLabel = hasLabel
    ? { "aria-labelledby": labelId }
    : { "aria-label": t("click_to_upload") };

  return (
    <div
      className={cn(
        "flex w-full min-w-0 flex-col gap-1.5",
        compact && "max-w-[560px]"
      )}
    >
      {hasLabel && (
        <FieldTitle id={labelId}>
          {label}
        </FieldTitle>
      )}
      {/* Drop area built on the shadcn Empty panel. */}
      <Empty
        {...getRootProps()}
        className="gap-2 rounded-lg border border-dashed border-input p-4 [overflow-wrap:anywhere] outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
      >
        {hasFiles ? (
          <>
            {cleanFiles.length === 1 ? (
              renderPreview(cleanFiles[0], 0)
            ) : (
              <div className="grid max-h-[480px] w-full max-w-[720px] grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))] gap-4 overflow-y-auto">
                {cleanFiles.map((file, index) => renderPreview(file, index))}
              </div>
            )}

            <input
              {...getInputProps({ disabled, ...inputLabel })}
              className="hidden"
            />
            {renderFileErrors([...fileErrors, ...(error ? [error] : [])])}
            {renderButton()}
          </>
        ) : (
          <>
            {renderUploadArea()}
            {error && renderFileErrors([error])}
          </>
        )}
      </Empty>
    </div>
  );
};

MultipleFilesUpload.propTypes = {};

export default MultipleFilesUpload;
